import { useEffect, useRef, useState } from "react";

// Subtle count-up on scroll into view. Works with plain numbers or numbers embedded
// in a prefix/suffix (e.g. "$7.56M", "≈ $7.56M", "27,000", "2,000+"). Non-numeric
// strings (e.g. "170 → 430", "$200K → $700K") pass through unchanged.
export default function StatCountUp({ value, duration = 1400, className = "" }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);
  const started = useRef(false);

  useEffect(() => {
    const m = String(value).match(/^([^0-9]*)([0-9][0-9,\.]*)([^0-9].*)?$/);
    if (!m || String(value).includes("→")) { setDisplay(value); return; }
    const prefix = m[1] || "", suffix = m[3] || "";
    const numeric = parseFloat(m[2].replace(/,/g, ""));
    if (!isFinite(numeric)) { setDisplay(value); return; }
    const isDecimal = /\./.test(m[2]);
    const grouped = /,/.test(m[2]);

    const format = (n) => {
      let out = isDecimal ? n.toFixed(2) : Math.round(n).toString();
      if (grouped) out = Number(isDecimal ? Number(out) : parseInt(out, 10)).toLocaleString("en-US" + (isDecimal ? "" : ""));
      return prefix + out + suffix;
    };

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const tick = (t) => {
            const p = Math.min(1, (t - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setDisplay(format(numeric * eased));
            if (p < 1) requestAnimationFrame(tick);
            else setDisplay(format(numeric));
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.4 });

    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [value, duration]);

  return <span ref={ref} className={className}>{display}</span>;
}
